import { useState, useEffect, FC } from 'react';
import { Button, Text } from '@livechat/design-system-react-components';

import { Product } from 'types/product';
import { PRODUCTS } from 'constants/product';
import { useOrganizations } from 'hooks/use-organizations';
import { useProducts } from 'hooks/use-products';
import Location from 'services/Location';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { getProductName, detectProduct, getAppLinkByName } from 'helpers/product';
import { canActivateProduct } from 'helpers/account';
import { getItem } from 'helpers/storage/local-storage';
import { parseJwt } from 'helpers/token';

import { useGetMe } from 'hooks/use-get-me';
import { DefaultChooserContent } from './default-chooser/DefaultChooserContent';
import { ExternalAppChooserContent } from './external-app-chooser-content/ExternalAppChooserContent';
import { ConfirmActivateProductContent } from './confirm-activate-product-content/ConfirmActivateProductContent';
import { OrganizationDetails } from './organization-details/OrganizationDetails';
import { ChooserLayout } from './chooser-layout/ChooserLayout';
import { ProductInOtherOrganization } from './product-in-other-organization/ProductInOtherOrganization';
import { VerificationTile } from './verification-tile/VerificationTile';

import * as styles from './styles';
import { getShouldDisplayExternalChooser } from './chooser-layout/helpers';

type Props = {
  email?: string;
  isAccountWhitelisted?: boolean;
  isAccountWhitelistedElsewhere?: boolean;
  isAccountVerified: boolean;
  showOnlyWhitelistedOrganizations?: boolean;
  onSelectNewTrial?(): void;
  onJoinBlocked(organizationId: string): void;
};

export const OrganizationChooser: FC<Props> = ({
  email,
  isAccountWhitelisted = false,
  isAccountWhitelistedElsewhere = false,
  showOnlyWhitelistedOrganizations = false,
  isAccountVerified,
  onSelectNewTrial,
  onJoinBlocked,
}) => {
  const currentProduct = getProductName(detectProduct());
  const productToActivate = Location.getHashParam('product') || currentProduct;
  const isSigningUp = getItem('signup') === 1 && PRODUCTS.includes(productToActivate);

  const [displayExternalChooser, setDisplayExternalChooser] = useState(false);
  const [isSwitchedToDefaultChooser, setIsSwitchedToDefaultChooser] = useState(false);

  const { roles } = useGetMe();
  const { organizations } = useOrganizations();
  const { getRedirectUrl, isFetchingProducts, products } = useProducts();

  useEffect(() => {
    if (isFetchingProducts) {
      return;
    }

    const state = Location.getHashParam('state');
    const token = state && parseJwt(state);
    const originalClientId = token?.client_id;

    const shouldDisplayExternalChooser = getShouldDisplayExternalChooser(
      originalClientId,
      products
    );

    if (!shouldDisplayExternalChooser) {
      setDisplayExternalChooser(false);
    } else {
      setDisplayExternalChooser(true);
    }
  }, [products, isFetchingProducts]);

  const switchToDefaultChooser = () => {
    setIsSwitchedToDefaultChooser(true);
  };

  const handleStartTrial = (): void => {
    pushExternalRedirectToDataLayer(getAppLinkByName(currentProduct));

    if (onSelectNewTrial) {
      onSelectNewTrial();
    }
  };

  const organizationsInWhichCanActivateProduct = organizations?.filter(
    (organization) =>
      organization.products.every((product) => product.name !== productToActivate) &&
      canActivateProduct({
        productToActivate,
        organization,
        accountRoles: roles,
        isAccountWhitelisted,
      })
  );

  const sortedOrganizations = [...organizations].sort((a, b): number => {
    if (currentProduct !== getProductName(Product.Undefined)) {
      const aHasProduct = a.products?.some((prod) => prod.name === currentProduct);
      const bHasProduct = b.products?.some((prod) => prod.name === currentProduct);
      if (aHasProduct && !bHasProduct) {
        return -1;
      }
      if (!aHasProduct && bHasProduct) {
        return 1;
      }
      return 0;
    }
    return 0;
  });

  const organizationsWithProduct = organizations.filter((organization) =>
    organization.products?.some((product) => product.name === productToActivate)
  );

  const shouldDisplaySignupChooser =
    !!organizationsInWhichCanActivateProduct?.length &&
    !isSwitchedToDefaultChooser &&
    isSigningUp &&
    currentProduct !== getProductName(Product.TextApp); // Force non-signup chooser for Text App

  if (!organizations.length || isFetchingProducts) {
    return null;
  }

  if ((!isAccountVerified || shouldDisplaySignupChooser) && isAccountWhitelisted) {
    return (
      <div>
        <ChooserLayout
          organizations={organizations}
          isOnActivate={shouldDisplaySignupChooser}
          isAccountWhitelisted={isAccountWhitelisted}
        >
          <div css={styles.container}>
            <div css={styles.organizationsContainer}>
              {sortedOrganizations
                ?.filter(
                  ({ whitelisted }) =>
                    (showOnlyWhitelistedOrganizations && whitelisted) ||
                    !showOnlyWhitelistedOrganizations
                )
                .map((organization) => (
                  <OrganizationDetails
                    organizationName={organization.name}
                    createdAt={organization.created_at}
                    key={organization.organization_id}
                  >
                    <DefaultChooserContent
                      organization={organization}
                      isAccountWhitelisted={isAccountWhitelisted}
                      onJoinBlocked={onJoinBlocked}
                    />
                  </OrganizationDetails>
                ))}
              {!isAccountVerified && isAccountWhitelistedElsewhere && (
                <VerificationTile email={email} />
              )}
            </div>
          </div>
          {!!onSelectNewTrial &&
            (currentProduct !== getProductName(Product.Undefined) ? (
              <div css={styles.startNewTrial}>
                <Text>Do you want to start {currentProduct} trial in new organization?</Text>
                <Button
                  kind="link"
                  href={getRedirectUrl(
                    currentProduct,
                    organizations.find((org) => org.products?.length === 0).organization_id
                  )}
                  onClick={handleStartTrial}
                >
                  Start {currentProduct} trial
                </Button>
              </div>
            ) : (
              <div css={styles.startNewTrial}>
                <Text>Do you want to start product trial in new organization?</Text>
                <Button kind="link" onClick={handleStartTrial}>
                  Start new trial
                </Button>
              </div>
            ))}
        </ChooserLayout>
      </div>
    );
  }

  return (
    <div>
      <ChooserLayout organizations={organizations} isOnActivate={shouldDisplaySignupChooser}>
        {shouldDisplaySignupChooser ? (
          <>
            <div css={styles.container}>
              <ConfirmActivateProductContent
                organizationsInWhichCanActivateProduct={organizationsInWhichCanActivateProduct}
                productToActivate={productToActivate}
              />
            </div>
            {organizationsWithProduct.length > 0 && (
              <ProductInOtherOrganization
                organizationsWithProduct={organizationsWithProduct}
                productToActivate={productToActivate}
                switchToDefaultChooser={switchToDefaultChooser}
              />
            )}
          </>
        ) : (
          <div css={styles.container}>
            <div css={styles.organizationsContainer}>
              {displayExternalChooser
                ? sortedOrganizations
                    ?.filter((organization) =>
                      roles.some((role) => role.organization_id === organization.organization_id)
                    )
                    .map((organization) => (
                      <OrganizationDetails
                        organizationName={organization.name}
                        createdAt={organization.created_at}
                        key={organization.organization_id}
                      >
                        <ExternalAppChooserContent organization={organization} />
                      </OrganizationDetails>
                    ))
                : sortedOrganizations?.map((organization) => (
                    <OrganizationDetails
                      organizationName={organization.name}
                      createdAt={organization.created_at}
                      key={organization.organization_id}
                    >
                      <DefaultChooserContent
                        organization={organization}
                        isAccountWhitelisted={isAccountWhitelisted}
                        onJoinBlocked={onJoinBlocked}
                      />
                    </OrganizationDetails>
                  ))}
            </div>
          </div>
        )}
      </ChooserLayout>
    </div>
  );
};
